<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    :closable="true"
    :dismissableMask="false"
    :style="{ width: '500px' }"
    header="Investment Assistance Required"
    class="buy-alert-dialog"
  >
    <div class="alert-content">
      <!-- Icon Section -->
      <div class="alert-icon-section">
        <i class="pi pi-info-circle alert-icon"></i>
      </div>

      <!-- Message Section -->
      <div class="alert-message">
        <h3 class="alert-title">Direct Purchase Not Available</h3>
        <p class="alert-description">
          We currently don't support direct online purchase for
          <strong>{{ investmentData.assetClass }}</strong> investments through our platform.
        </p>
      </div>

      <!-- Investment Details -->
      <div class="investment-details">
        <div class="detail-row">
          <span class="detail-label">Security:</span>
          <span class="detail-value">{{ investmentData.securityName }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Recommended Allocation:</span>
          <span class="detail-value">
            ₹{{ formatAmount(investmentData.allocatedAmount) }}
            ({{ investmentData.allocationPercentage }}%)
          </span>
        </div>
      </div>

      <!-- Next Steps -->
      <div class="next-steps">
        <h4 class="steps-title">Next Steps:</h4>
        <ul class="steps-list">
          <li>Our investment team will contact you shortly</li>
          <li>We'll help you invest in {{ investmentData.assetClass }}</li>
          <li>You can also reach out to us directly</li>
        </ul>
      </div>
    </div>

    <template #footer>
      <div class="dialog-footer">
        <ANButton
          label="Close"
          variant="secondary"
          @click="handleClose"
          icon="pi pi-times"
        />
        <ANButton
          label="Contact Our Team"
          variant="primary"
          @click="handleContactTeam"
          icon="pi pi-phone"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { computed } from 'vue'
import Dialog from 'primevue/dialog'
import ANButton from '@/components/common/AnButton.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  investmentData: {
    type: Object,
    required: true,
    default: () => ({
      securityName: '',
      assetClass: '',
      allocatedAmount: 0,
      allocationPercentage: '0.0'
    })
  }
})

const emit = defineEmits(['update:modelValue', 'contact-team'])

const isVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const formatAmount = (value) => {
  if (!value) return '0'
  return new Intl.NumberFormat('en-IN').format(Math.round(value))
}

const handleClose = () => {
  isVisible.value = false
}

const handleContactTeam = () => {
  emit('contact-team')
  isVisible.value = false
}
</script>

<style scoped>
.alert-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.alert-icon-section {
  display: flex;
  justify-content: center;
}

.alert-icon {
  font-size: 4rem;
  color: #f59e0b;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

.alert-message {
  text-align: center;
}

.alert-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0 0 12px 0;
}

.alert-description {
  font-size: 1rem;
  color: var(--an-text-secondary);
  line-height: 1.5;
  margin: 0;
}

.alert-description strong {
  color: var(--an-text-primary);
  font-weight: 600;
}

.investment-details {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.detail-label {
  font-size: 0.9rem;
  color: var(--an-text-secondary);
  font-weight: 500;
}

.detail-value {
  font-size: 0.95rem;
  color: var(--an-text-primary);
  font-weight: 600;
}

.next-steps {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #0ea5e9;
  border-radius: 8px;
  padding: 16px;
}

.steps-title {
  font-size: 1rem;
  font-weight: 600;
  color: #0c4a6e;
  margin: 0 0 12px 0;
}

.steps-list {
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.steps-list li {
  font-size: 0.9rem;
  color: #1e40af;
  line-height: 1.4;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px 24px;
}

/* Dialog Header Styling */
.buy-alert-dialog :deep(.p-dialog-header) {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-bottom: 2px solid #f59e0b;
  padding: 20px 24px;
}

.buy-alert-dialog :deep(.p-dialog-title) {
  font-size: 1.3rem;
  font-weight: 700;
  color: #92400e;
}

.buy-alert-dialog :deep(.p-dialog-content) {
  padding: 0;
}
</style>
